/**
 * Wellness score scales.
 *
 * Players rate 1–5 (the phone app always has; the web form moved from 1–7 to
 * 1–5 on 2026-10-11 so coaches and players read the same numbers). Older web
 * rows are 1–7 and carry scale_max = 7 or NULL. Every display converts an
 * entry to DISPLAY_SCALE first — never mix raw values from different eras.
 *
 * LEGACY_SCALE mirrors WELLNESS_DEFAULT_SCALE in
 * personal-training-BE/lib/wellness-alert-lib.php.
 */

export const DISPLAY_SCALE = 5;
export const LEGACY_SCALE = 7;
/** @deprecated kept for older imports — the display scale is now 5 */
export const WEB_SCALE = DISPLAY_SCALE;
export const TEN_SCALE = 10;
export const METRIC_KEYS = ["sleep", "happyness", "stress", "fatigue", "muscle_soreness"];

/** The scale an entry was recorded on. */
export function entryScale(entry) {
  const s = Number(entry && entry.scale_max);
  return s >= 2 && s <= 10 ? s : LEGACY_SCALE;
}

/** Convert one value between scales, to one decimal place. */
export function convertScore(value, fromScale, toScale) {
  const v = Number(value);
  if (!Number.isFinite(v)) return 0;
  return Math.round((v / fromScale) * toScale * 10) / 10;
}

/**
 * Copy of the entry with the five metrics converted to `toScale`.
 * Keeps every other field (id, date, player_id, ...) untouched.
 */
export function normaliseEntry(entry, toScale = DISPLAY_SCALE) {
  if (!entry) return entry;
  const from = entryScale(entry);
  const out = { ...entry, scale_max: toScale };
  METRIC_KEYS.forEach((k) => {
    if (entry[k] !== undefined && entry[k] !== null) {
      out[k] = convertScore(entry[k], from, toScale);
    }
  });
  return out;
}

/** Average of the five metrics on `toScale`, one decimal place. */
export function entryAverage(entry, toScale = TEN_SCALE) {
  const n = normaliseEntry(entry, toScale);
  const vals = METRIC_KEYS.map((k) => Number(n[k])).filter((v) => Number.isFinite(v));
  if (!vals.length) return null;
  return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10;
}

/**
 * Band on a 0–10 scale, matching the calendar legend and the backend alert:
 * 7–10 good, 4–6.9 okay, 0–3.9 struggling.
 */
export function bandForTen(score) {
  if (score >= 7) return "good";
  if (score >= 4) return "mid";
  return "low";
}
